/**
 * DataTableEnhanced Demo
 *
 * A self-contained demonstration of the three-tier grid architecture:
 *   Page (state orchestration)
 *     -> Grid (column definitions + cell renderers)
 *       -> DataTableEnhanced (generic table renderer)
 *
 * Use this as a copy-paste template when creating new grids.
 * All features demonstrated: multi-column sorting, column visibility,
 * pagination, loading states, and empty states.
 */

import { useState, useMemo, useCallback } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/page-header';
import { PaginationControls } from '@/components/pagination-controls';
import {
  DataTableEnhanced,
  columnDefinitionsToVisibility,
  type ColumnSort,
} from '@/components/ui/data-table-enhanced';
import { ColumnConfiguratorDrawer, type ColumnDefinition } from '@/components/column-configurator-drawer';
import { calculatePagination } from '@/types/pagination';
import { formatCurrency } from '@/lib/utils';
import {
  Search,
  X,
  Settings2,
  Info,
  CheckCircle2,
  Code2,
  RefreshCw,
} from 'lucide-react';

// =====================================================================
// TIER 1: Data types and mock data (would normally come from API)
// =====================================================================

interface Employee {
  id: number;
  name: string;
  email: string;
  department: string;
  role: string;
  salary: number;
  startDate: string;
  status: 'Active' | 'On Leave' | 'Terminated';
  utilisation: number;
  projectCount: number;
  location: string;
  manager: string;
}

const DEPARTMENTS = ['Engineering', 'Design', 'Marketing', 'Finance', 'Operations', 'Sales', 'HR', 'Legal'];
const ROLES = ['Developer', 'Designer', 'Manager', 'Analyst', 'Director', 'Coordinator', 'Specialist', 'Lead'];
const LOCATIONS = ['London', 'Manchester', 'Edinburgh', 'Leeds', 'Bristol', 'Birmingham', 'Glasgow', 'Remote'];
const STATUSES: Employee['status'][] = ['Active', 'Active', 'Active', 'Active', 'On Leave', 'Terminated'];
const FIRST_NAMES = ['Alice', 'Bob', 'Charlie', 'Diana', 'Edward', 'Fiona', 'George', 'Hannah', 'Ian', 'Julia',
  'Kevin', 'Laura', 'Mike', 'Nina', 'Oscar', 'Paula', 'Quinn', 'Rachel', 'Sam', 'Tina',
  'Umar', 'Vera', 'Will', 'Xena', 'Yuri', 'Zoe', 'Aaron', 'Beth', 'Carl', 'Daisy'];
const LAST_NAMES = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Wilson', 'Moore',
  'Taylor', 'Anderson', 'Thomas', 'Jackson', 'White', 'Harris', 'Martin', 'Thompson', 'Young', 'Allen'];

function seededRng(seed: number) {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function generateEmployees(count: number): Employee[] {
  const rng = seededRng(123);
  const pick = <T,>(arr: readonly T[]): T => arr[Math.floor(rng() * arr.length)];

  return Array.from({ length: count }, (_, i) => {
    const firstName = pick(FIRST_NAMES);
    const lastName = pick(LAST_NAMES);
    const dept = pick(DEPARTMENTS);
    const year = 2018 + Math.floor(rng() * 7);
    const month = String(Math.floor(rng() * 12) + 1).padStart(2, '0');
    const day = String(Math.floor(rng() * 28) + 1).padStart(2, '0');

    return {
      id: i + 1,
      name: `${firstName} ${lastName}`,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@company.com`,
      department: dept,
      role: pick(ROLES),
      salary: Math.round((35000 + rng() * 85000) / 1000) * 1000,
      startDate: `${year}-${month}-${day}`,
      status: pick(STATUSES),
      utilisation: Math.round(rng() * 100),
      projectCount: Math.floor(rng() * 8),
      location: pick(LOCATIONS),
      manager: `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`,
    };
  });
}

const MOCK_EMPLOYEES = generateEmployees(120);

// =====================================================================
// TIER 2: Grid component (column definitions + cell renderers)
// This would normally be in a separate file like `components/employee-grid.tsx`
// =====================================================================

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'name', name: 'Name', description: 'Employee name', category: 'Resource', visible: true, locked: true, order: 0 },
  { id: 'email', name: 'Email', description: 'Email address', category: 'Resource', visible: true, order: 1 },
  { id: 'department', name: 'Department', description: 'Department', category: 'Resource', visible: true, order: 2 },
  { id: 'role', name: 'Role', description: 'Job role', category: 'Resource', visible: true, order: 3 },
  { id: 'status', name: 'Status', description: 'Employment status', category: 'Status', visible: true, order: 4 },
  { id: 'salary', name: 'Salary', description: 'Annual salary', category: 'Financial', visible: true, order: 5, align: 'right' },
  { id: 'utilisation', name: 'Utilisation', description: 'Current utilisation %', category: 'Performance', visible: true, order: 6, align: 'right' },
  { id: 'projectCount', name: 'Projects', description: 'Active project count', category: 'Performance', visible: true, order: 7, align: 'right' },
  { id: 'location', name: 'Location', description: 'Office location', category: 'Resource', visible: false, order: 8 },
  { id: 'manager', name: 'Manager', description: 'Line manager', category: 'Resource', visible: false, order: 9 },
  { id: 'startDate', name: 'Start Date', description: 'Employment start date', category: 'Timeline', visible: false, order: 10 },
];

function getStatusVariant(status: Employee['status']): 'default' | 'secondary' | 'destructive' {
  switch (status) {
    case 'Active': return 'default';
    case 'On Leave': return 'secondary';
    case 'Terminated': return 'destructive';
  }
}

function buildEmployeeColumns(): ColumnDef<Employee, unknown>[] {
  return [
    {
      id: 'name',
      accessorFn: (row) => row.name,
      header: 'Name',
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    {
      id: 'email',
      accessorFn: (row) => row.email,
      header: 'Email',
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">{row.original.email}</span>
      ),
    },
    {
      id: 'department',
      accessorFn: (row) => row.department,
      header: 'Department',
      cell: ({ row }) => <Badge variant="outline">{row.original.department}</Badge>,
    },
    {
      id: 'role',
      accessorFn: (row) => row.role,
      header: 'Role',
      cell: ({ row }) => row.original.role,
    },
    {
      id: 'status',
      accessorFn: (row) => row.status,
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={getStatusVariant(row.original.status)}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      id: 'salary',
      accessorFn: (row) => row.salary,
      header: 'Salary',
      meta: { align: 'right' },
      cell: ({ row }) => formatCurrency(row.original.salary),
    },
    {
      id: 'utilisation',
      accessorFn: (row) => row.utilisation,
      header: 'Utilisation',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const val = row.original.utilisation;
        const color = val >= 80 ? 'text-green-600' : val >= 50 ? 'text-amber-600' : 'text-red-600';
        return <span className={color}>{val}%</span>;
      },
    },
    {
      id: 'projectCount',
      accessorFn: (row) => row.projectCount,
      header: 'Projects',
      meta: { align: 'right' },
      cell: ({ row }) => row.original.projectCount,
    },
    {
      id: 'location',
      accessorFn: (row) => row.location,
      header: 'Location',
      cell: ({ row }) => row.original.location,
    },
    {
      id: 'manager',
      accessorFn: (row) => row.manager,
      header: 'Manager',
      cell: ({ row }) => row.original.manager,
    },
    {
      id: 'startDate',
      accessorFn: (row) => row.startDate,
      header: 'Start Date',
      cell: ({ row }) => row.original.startDate,
    },
  ];
}

interface EmployeeGridProps {
  data: Employee[];
  columnDefinitions: ColumnDefinition[];
  columnSorts: ColumnSort[];
  onColumnSortChange: (sorts: ColumnSort[]) => void;
  loading?: boolean;
  hasActiveFilters?: boolean;
  paginationContent?: React.ReactNode;
}

function EmployeeGrid({
  data,
  columnDefinitions,
  columnSorts,
  onColumnSortChange,
  loading = false,
  hasActiveFilters = false,
  paginationContent,
}: EmployeeGridProps) {
  const allColumns = useMemo(() => buildEmployeeColumns(), []);

  const visibleColumnDefs = useMemo(() => {
    const visibleDefs = columnDefinitions
      .filter((cd) => cd.visible)
      .sort((a, b) => a.order - b.order);
    const columnMap = new Map(allColumns.map((c) => [c.id, c]));
    return visibleDefs
      .map((cd) => columnMap.get(cd.id))
      .filter((c): c is ColumnDef<Employee, unknown> => c !== undefined);
  }, [columnDefinitions, allColumns]);

  const columnVisibility = useMemo(
    () => columnDefinitionsToVisibility(columnDefinitions),
    [columnDefinitions],
  );

  const emptyMessage = hasActiveFilters ? (
    <div className="space-y-2">
      <p className="text-lg font-medium">No employees found</p>
      <p className="text-sm">Try adjusting your search term</p>
    </div>
  ) : (
    <div className="space-y-2">
      <p className="text-lg font-medium">No employees</p>
      <p className="text-sm">No employee data available</p>
    </div>
  );

  return (
    <DataTableEnhanced<Employee, unknown>
      columns={visibleColumnDefs}
      data={data}
      columnVisibility={columnVisibility}
      columnSorts={columnSorts}
      onColumnSortChange={onColumnSortChange}
      loading={loading}
      loadingRowCount={10}
      emptyMessage={emptyMessage}
      getRowId={(row) => String(row.id)}
      dataTour="employee-demo-table"
      paginationContent={paginationContent}
    />
  );
}

// =====================================================================
// TIER 3: Page component (state orchestration, filters, pagination)
// =====================================================================

function getColumnValue(id: string, emp: Employee): string | number | boolean | undefined {
  switch (id) {
    case 'name': return emp.name;
    case 'email': return emp.email;
    case 'department': return emp.department;
    case 'role': return emp.role;
    case 'status': return emp.status;
    case 'salary': return emp.salary;
    case 'utilisation': return emp.utilisation;
    case 'projectCount': return emp.projectCount;
    case 'location': return emp.location;
    case 'manager': return emp.manager;
    case 'startDate': return emp.startDate;
    default: return undefined;
  }
}

export function DataTableDemo() {
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([]);
  const [columns, setColumns] = useState<ColumnDefinition[]>(COLUMN_DEFS);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);

  // Simulate loading
  const simulateLoading = useCallback(() => {
    setLoading(true);
    setTimeout(() => setLoading(false), 1500);
  }, []);

  // Filter
  const filteredData = useMemo(() => {
    if (!searchTerm) return MOCK_EMPLOYEES;
    const term = searchTerm.toLowerCase();
    return MOCK_EMPLOYEES.filter(emp =>
      [emp.name, emp.email, emp.department, emp.role, emp.location]
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }, [searchTerm]);

  // Sort + paginate
  const paginatedData = useMemo(() => {
    const data = [...filteredData];

    if (columnSorts.length > 0) {
      data.sort((a, b) => {
        for (const { columnId, direction } of columnSorts) {
          const aVal = getColumnValue(columnId, a);
          const bVal = getColumnValue(columnId, b);
          if (aVal === undefined && bVal === undefined) continue;
          if (aVal === undefined) return 1;
          if (bVal === undefined) return -1;
          let cmp = 0;
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            cmp = aVal - bVal;
          } else {
            cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' });
          }
          if (cmp !== 0) return direction === 'asc' ? cmp : -cmp;
        }
        return 0;
      });
    }

    const start = (currentPage - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [filteredData, columnSorts, currentPage, pageSize]);

  const pagination = useMemo(
    () => calculatePagination(filteredData.length, currentPage, pageSize),
    [filteredData.length, currentPage, pageSize],
  );

  const hasActiveFilters = searchTerm !== '';

  // Reset page when search changes
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const paginationContent = pagination.totalItems > 0 ? (
    <PaginationControls
      pagination={pagination}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
      showPageSizeSelector
      className="pt-4 border-t"
    />
  ) : null;

  return (
    <div className="container mx-auto py-6 space-y-6">
      <PageHeader
        title="DataTableEnhanced Demo"
        description="Three-tier grid architecture: Page > Grid > DataTableEnhanced"
      />

      {/* Architecture explanation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 text-primary" />
            Three-Tier Grid Architecture
          </CardTitle>
          <CardDescription>
            Based on EBTimeEntry's Timesheet Reports pattern - a reusable, composable approach to data grids
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2 p-4 rounded-lg border bg-card">
              <h4 className="font-semibold flex items-center gap-2">
                <Code2 className="h-4 w-4 text-blue-500" />
                Tier 1: Page Component
              </h4>
              <p className="text-sm text-muted-foreground">
                Owns all state: data fetching, filtering, sorting, pagination.
                Passes data and callbacks down to the Grid.
              </p>
              <Badge variant="outline" className="text-xs">DataTableDemo (this file)</Badge>
            </div>
            <div className="space-y-2 p-4 rounded-lg border bg-card">
              <h4 className="font-semibold flex items-center gap-2">
                <Code2 className="h-4 w-4 text-green-500" />
                Tier 2: Grid Component
              </h4>
              <p className="text-sm text-muted-foreground">
                Defines column definitions with TanStack ColumnDef and cell renderers.
                Maps ColumnDefinition visibility to TanStack state.
              </p>
              <Badge variant="outline" className="text-xs">EmployeeGrid (inline here, separate file in prod)</Badge>
            </div>
            <div className="space-y-2 p-4 rounded-lg border bg-card">
              <h4 className="font-semibold flex items-center gap-2">
                <Code2 className="h-4 w-4 text-purple-500" />
                Tier 3: DataTableEnhanced
              </h4>
              <p className="text-sm text-muted-foreground">
                Generic, reusable table renderer. Handles sorting UI, loading skeletons,
                empty states, column visibility. Zero domain knowledge.
              </p>
              <Badge variant="outline" className="text-xs">components/ui/data-table-enhanced.tsx</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feature highlights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-success-600" />
            Features Demonstrated
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-4">
            {[
              'Multi-column sorting with priority badges',
              'Column visibility via ColumnConfiguratorDrawer',
              'Client-side search filtering',
              'Pagination with page size selector',
              'Loading skeleton states',
              'Empty state messages',
              'Column alignment (left/right)',
              'Badge-based cell renderers',
            ].map((feature) => (
              <div key={feature} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="h-3.5 w-3.5 text-success-600 mt-0.5 flex-shrink-0" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* The actual grid demo */}
      <Card>
        <CardHeader>
          <CardTitle>Employee Directory ({filteredData.length} employees)</CardTitle>
          <CardDescription>
            Click column headers to sort. Click again to cycle: asc &rarr; desc &rarr; remove.
            Multi-column sort shows priority badges.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Toolbar */}
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9 pr-9"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleSearch('')}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
            <Button variant="outline" onClick={simulateLoading}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Simulate Loading
            </Button>
            <Button variant="outline" onClick={() => setColumnConfigOpen(true)}>
              <Settings2 className="h-4 w-4 mr-2" />
              Configure Columns
            </Button>
          </div>

          {/* Grid */}
          <EmployeeGrid
            data={paginatedData}
            columnDefinitions={columns}
            columnSorts={columnSorts}
            onColumnSortChange={setColumnSorts}
            loading={loading}
            hasActiveFilters={hasActiveFilters}
            paginationContent={paginationContent}
          />
        </CardContent>
      </Card>

      {/* Column configurator */}
      <ColumnConfiguratorDrawer
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onSave={setColumns}
      />
    </div>
  );
}
